import { ChangeDetectionStrategy, Component, inject, Injector, ViewChild, ViewContainerRef } from "@angular/core";
import { ClientSoundService } from "./shared/services/sound/client-sound.service";
import { ErrorPageService } from "./error-page/error-page.service";
import { AppStartUseCase } from "./app-start.use-case";
import { IosFullscreenHintService } from "@karma-games/angular-devkit/src/lib/modules/ios-fullscreen";
import { IErrorPageData } from "./error-page/interfaces";
import { EAvatarAlignment } from "@karma-games/angular-devkit/src/lib/modules/user-info";
import { ETopBarButton, TOP_BAR_CONFIG, TopBarComponent } from "@karma-games/angular-devkit/src/lib/modules/top-bar";
import { EControlType } from "@karma-games/angular-devkit/src/lib/interfaces";
import { ICustomPopup } from "@karma-games/angular-devkit/src/lib/modules/popup";
import { GameLimitsComponent } from "./game-limits/game-limits.component";
import { GameRulesComponent } from "./game-rules/game-rules.component";
import { EClientSettings } from "@karma-games/angular-devkit/src/lib/modules/settings";
import { GetConfigUseCase } from "./core/config/get-config.use-case";
import {
    ESideMenuDirection,
    SIDE_MENU_CONFIG,
    SIDE_MENU_OPEN_DATA
} from "@karma-games/angular-devkit/src/lib/modules/side-menu";
import { isIphoneSafari } from "@karma-games/angular-devkit/src/lib/services/browser-detect";
import { PreventSleepService } from "@karma-games/angular-devkit/src/lib/services/prevent-sleep";
import {
    EPresenterType,
    PresenterClusterService
} from "@karma-games/angular-devkit/src/lib/services/presenter-cluster";
import { SplashController } from "@karma-games/angular-devkit/src/lib/modules/splash";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: TOP_BAR_CONFIG, useFactory: () => {
                return {
                    buttons: [
                        ETopBarButton.MenuButton,
                        ETopBarButton.Mute
                    ],
                    avatarAlignment: EAvatarAlignment.Right,
                    balanceAnimated: false,
                    logoImg: `assets/images/logo.svg?${Date.now()}`
                };
            }
        },
        {
            provide: SIDE_MENU_CONFIG, useFactory: async () => {
                const configUseCase = inject(GetConfigUseCase);
                const config = await configUseCase.execute();
                return {
                    languages: config?.languages ?? [],
                    logoUrl: `assets/images/logo.svg?${Date.now()}`,
                    clientMenuItemModels: {
                        "gameLimits": {
                            id: "gameLimits",
                            key: "SETTINGS.GAME_LIMITS",
                            controlType: EControlType.PopupLink,
                            value: <ICustomPopup>{
                                content: GameLimitsComponent,
                                options: {
                                    styleProperties: {
                                        minWidth: "11em",
                                        width: "15em",
                                        height: "fit-content",
                                        maxWidth: "90vw"
                                    }
                                }
                            }
                        },
                        "gameRules": {
                            id: "gameRules",
                            key: "SETTINGS.GAME_RULES",
                            controlType: EControlType.PopupLink,
                            value: {
                                content: GameRulesComponent,
                                options: {
                                    styleProperties: {
                                        height: "fit-content",
                                        maxHeight: "92vh",
                                        width: "fit-content",
                                        maxWidth: "92vw"
                                    }
                                }
                            }
                        }
                    }
                };
            }
        },
        {
            provide: SIDE_MENU_OPEN_DATA,
            useFactory: (injector: Injector) => {
                const settingsControlsArr: (EClientSettings | string)[] = [
                    EClientSettings.Lang,
                    EClientSettings.Music,
                    EClientSettings.SoundEffects,
                    EClientSettings.ForceCanvas,
                    EClientSettings.NoAnimation,
                    "gameLimits",
                    "gameRules"
                ];
                if (!isIphoneSafari(injector)) {
                    settingsControlsArr.push(EClientSettings.Fullscreen);
                }
                settingsControlsArr.push(EClientSettings.BackToHome);


                return {
                    requestedSettings: settingsControlsArr,
                    sideMenuDirection: ESideMenuDirection.Left
                };
            }, deps: [Injector]
        }
    ]
})
export class AppComponent {
    title = "bluecroco-rlgl-client";
    @ViewChild("rootViewContainerRef", {
        static: false,
        read: ViewContainerRef
    }) rootViewContainerRef?: ViewContainerRef;
    @ViewChild("topBar", {static: false}) topBar: TopBarComponent | undefined = undefined;
    public errorData: IErrorPageData | undefined = undefined;
    private splashController: SplashController = new SplashController();

    constructor(appStartUseCase: AppStartUseCase,
                _clientSoundService: ClientSoundService,
                _iosFullscreenHintService: IosFullscreenHintService,
                errorPageService: ErrorPageService,
                _preventSleepService: PreventSleepService,
                private presenterClusterService: PresenterClusterService) {
        errorPageService.subscribeShowErrorPage()
            .subscribe((errorData: IErrorPageData | undefined) => {
                this.errorData = errorData;
                if (errorData?.errorText === "ERROR_BANNED") {
                    this.splashController.showBan("ERROR_BANNED");
                } else {
                    this.splashController.showError();
                }
            });
        appStartUseCase.execute();
    }

    ngAfterViewInit(): void {
        this.splashController.hideSplash();
        this.presenterClusterService.addPresenter({
            id: "top-bar",
            ref: <TopBarComponent>this.topBar,
            type: EPresenterType.BalanceView
        });
    }
}
